import { VehicleBrand, VehicleDTO } from '../models/vehicle.models';

// eslint-disable-next-line no-use-before-define
export type IVehicle = Omit<Vehicle, 'id' | 'v_id' | 'vin' | 'toDTO'>;

/**
 * Vehicle Class
 */
export class Vehicle {
  brand?: VehicleBrand;
  license_plate?: string;
  display_name?: string;
  original_name?: string;
  model?: string;
  color?: string;
  description?: string;
  fleets?: number[];
  low_battery_alert_threshold?: number;
  overcharge_alert_threshold?: number;
  speed_alert_threshold?: number;
  archived?: boolean;
  user_color?: string;
  premium?: boolean;
  state?: string;
  anti_theft_state?: 'on' | 'pt_on' | 'p_on' | 'off' | 'p_off';
  token_id?: number;
  model_year?: number;
  firmware_version?: string;
  configured_virtual_key?: boolean;
  unsupported_hardware?: boolean;
  unsupported_firmware?: boolean;
  is_untracked?: boolean;

  constructor(
    public id: number | string,
    public v_id: number | string,
    public vin: string,
    private readonly options?: IVehicle
  ) {
    if (options !== undefined) {
      this.brand = options.brand;
      this.license_plate = options.license_plate;
      this.display_name = options.display_name;
      this.original_name = options.original_name;
      this.model = options.model;
      this.color = options.color;
      this.description = options.description;
      this.fleets = options.fleets;
      this.low_battery_alert_threshold = options.low_battery_alert_threshold;
      this.overcharge_alert_threshold = options.overcharge_alert_threshold;
      this.speed_alert_threshold = options.speed_alert_threshold;
      this.archived = options.archived;
      this.user_color = options.user_color;
      this.model_year = options.model_year;
      this.firmware_version = options.firmware_version;
      this.configured_virtual_key = options.configured_virtual_key;
      this.unsupported_firmware = options.unsupported_firmware;
      this.unsupported_hardware = options.unsupported_hardware;
      this.anti_theft_state = options.anti_theft_state;
      this.is_untracked = options.is_untracked;
      this.token_id = options.token_id;
    }
  }

  /**
   * Returns a copy of the Vehicle object in a simplified form of VehicleDTO
   * without its original 'id' field
   */
  // eslint-disable-next-line no-use-before-define
  static toDTO(vehicle: Vehicle): VehicleDTO {
    // NOTE: code below is illegal, don't do it like this
    // eslint-disable-next-line no-use-before-define, @typescript-eslint/no-explicit-any
    const dto = { ...vehicle, brand: vehicle.brand?.name } as any;
    delete dto['id'];
    delete dto['options'];
    return dto;
  }

  /**
   * Very simple VIN validator function,
   * use this for trivial checks only, because this method may let invalid values through
   * if they have the format of a VIN but do not have a valid checksum.
   */
  static simpleValidateVIN(vin: string): boolean {
    const pattern = /^[^\Wioq]{17}$/;
    return !!vin.toLowerCase().match(pattern);
  }

  /**
   * VIN number validator
   * Stolen from: https://stackoverflow.com/questions/3831764/how-to-validate-a-vin-number
   * @deprecated Faulty, revisit when possible. Use simpleValidateVIN instead.
   */
  static validateVIN(vin: string): boolean {
    const isNumeric = (mixed_var: string | number): boolean => {
      return (
        (typeof mixed_var === 'number' || typeof mixed_var === 'string') &&
        mixed_var !== '' &&
        !isNaN(mixed_var as number)
      );
    };

    const pattern = /^[^\Wioq]{17}$/;
    const weights = [8, 7, 6, 5, 4, 3, 2, 10, 0, 9, 8, 7, 6, 5, 4, 3, 2];
    const transliterations: { [key: string]: number } = {
      a: 1,
      b: 2,
      c: 3,
      d: 4,
      e: 5,
      f: 6,
      g: 7,
      h: 8,
      j: 1,
      k: 2,
      l: 3,
      m: 4,
      n: 5,
      p: 7,
      r: 9,
      s: 2,
      t: 3,
      u: 4,
      v: 5,
      w: 6,
      x: 7,
      y: 8,
      z: 9,
    };

    vin = vin.toLowerCase();
    if (!vin.match(pattern)) return false;

    let sum = 0;
    for (let i = 0; i < vin.length; i++) {
      if (!isNumeric(vin.charAt(i))) sum += transliterations[vin.charAt(i)] * weights[i];
      else sum += parseInt(vin.charAt(i), 10) * weights[i];
    }

    let checkDigit: number | string = sum % 11;
    if (checkDigit === 10) {
      // check digit of 10 represented by X
      checkDigit = 'x';
    }

    return checkDigit === vin.charAt(8);
  }

  /**
   * Filter predicate for an array of Vehicles to be used in couple with [].filter()
   * This method **does not mutate** the original array of vehicles
   * @param list list of vehicles to filter through
   * @param _filter string value to filter by
   *  (may be VIN, License number, Display Name, Vehicle brand, model or color)
   */
  static filterPredicate(vehicle: Vehicle, _filter: string): boolean {
    /** No Space, No Line Breaks regular Expression */
    const rgx = / |\r?\n|\r/g;

    // filter by keyword (if any)
    const keyword = _filter.toLowerCase().replace(rgx, '');
    if (keyword !== '') {
      return !!(
        vehicle.vin.toLowerCase().replace(rgx, '').includes(keyword) ||
        vehicle.display_name?.toLowerCase().replace(rgx, '').includes(keyword) ||
        vehicle.license_plate?.toLowerCase().replace(rgx, '').includes(keyword) ||
        vehicle.brand?.name.toLowerCase().replace(rgx, '').includes(keyword) ||
        vehicle.model?.toLowerCase().replace(rgx, '').includes(keyword) ||
        vehicle.color?.toLowerCase().replace(rgx, '').includes(keyword)
      );
    }

    return true;
  }
}
